import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Card, Badge, Heading, Spinner,
} from 'framework-ui-components';
import ErroreIcon from 'framework-ui-components/Components/Icons/ErroreIcon';
import { IFrameClassName } from '../../../models/constants';
import usePluginHandler from '../../../hooks/usePluginHandler';
import useIFrameLoader from './useIFrameLoader';
import IFrameModalizer from './IFrameModalizer';
import useFocus from './useFocus';
import useIFrameInDialog from './useIFrameInDialog';
import useRoutingHandler from './useRoutingHandler';
import useForwardMessageToParent from './useForwardMessageToParent';
import useViewportResolutionSender from './useViewportResolutionSender';
import useGotoExternal from './useGotoExternal';
import useCallForward from './useCallForward';
import useClickOutside from './useClickOutside';
import useRootLocation from './useRootLocation';

const iframeDefaultStyle = {
  width: '100%',
  height: '100%',
  border: 0,
};

/**
Componente PARENT utilizzato per caricare un contenuto plugin.

Il componente gestisce i seguenti comportamenti:

- gestione del routing e allinemaneto url tra padre e figlio
- gestione del focus
- gestione del click "fuori" dall'area del child
- gestione dei child in dialog
- gestione delle risoluzione da propagare tra padre e figlio
- forwarding di messaggi e funzioni di go to

 */
const IFrame = ({
  src, updateHash, checkClickableOutside, style, showSpinner, srcParams, onRedirectUrl,
  onReceiveMessage, onDialogResize, onDialogOk, onDialogCancel, extraParams, checkLoaded, onLoaded, inMemoryRouting,
}) => {
  const { onLoaded: notifyLoadedToHashing, initialSrc } = useRoutingHandler(src, updateHash, srcParams, inMemoryRouting);

  // nel caso sia impostato il controllo checkClickableOutside l'evento click per
  // IFrameModalizer deve essere vincolato all'evento click di useClickOutside
  const iframeModalizerRef = useRef();
  const onClick = useCallback((event) => {
    if (iframeModalizerRef.current) {
      iframeModalizerRef.current.click(event);
    }
  }, []);

  useClickOutside(checkClickableOutside, onClick);
  useFocus();
  useIFrameInDialog(onDialogResize, onDialogOk, onDialogCancel);
  useViewportResolutionSender();
  useGotoExternal(onRedirectUrl);
  useCallForward();
  useRootLocation();
  usePluginHandler(onReceiveMessage);

  const handleLoadingSuccess = useCallback(() => {
    onLoaded && onLoaded(true);
    notifyLoadedToHashing();
  }, [notifyLoadedToHashing, onLoaded]);

  const handleLoadingFailure = useCallback(() => {
    onLoaded && onLoaded(false);
  }, [onLoaded]);

  const {
    onLoad, loading, loadingError,
  } = useIFrameLoader(initialSrc, extraParams, checkLoaded, handleLoadingSuccess, handleLoadingFailure);

  useForwardMessageToParent();

  return (
    loadingError
      ? (
        <Card title="Si è verificato un errore">
          <div className="errorBoundary">
            <div className="errorBoundary__content">
              <Badge alarm><ErroreIcon /></Badge>
              <Heading as="h2">Errore di caricamento della pagina</Heading>
              <Heading as="h5" className="errorHint">Ricarica la pagina. Se il problema persiste, contatta l&apos;assistenza.</Heading>
            </div>
          </div>
        </Card>
      )
      : (
        <React.Fragment>
          {loading && showSpinner && (
            <div style={{
              position: 'absolute',
              left: '50%',
              marginLeft: '-25px',
              top: '50%',
              marginTop: '-25px',
            }}
            >
              <Spinner />
            </div>
          )}
          <IFrameModalizer
            ref={iframeModalizerRef}
            clickFromRef={checkClickableOutside}
            className={IFrameClassName}
          >
            {initialSrc && (
              <iframe
                data-testid="iframe"
                title="iframe"
                style={{ ...iframeDefaultStyle, ...style }}
                className="embed-responsive-item"
                src={initialSrc}
                onLoad={onLoad}
              />
            )}
          </IFrameModalizer>
        </React.Fragment>
      )
  );
};

IFrame.defaultProps = {
  style: {},
  updateHash: true,
  onDialogResize: null,
  onDialogOk: null,
  onDialogCancel: null,
  checkLoaded: true,
  onLoaded: null,
  onReceiveMessage: null,
  showSpinner: true,
  srcParams: null,
  checkClickableOutside: false,
  onRedirectUrl: null,
  extraParams: null,
  inMemoryRouting: false,
};

IFrame.propTypes = {
  /** url da caricare (**senza parametri querystring**: per quelli usare srcParams) */
  src: PropTypes.string.isRequired,
  /** oggetto contentente le eventuali proprietà da passare
   * **via querystring** al primo caricamento dell'applicazione */
  srcParams: PropTypes.shape({}),
  /** oggetto contentente le eventuali proprietà da passare
   * via messaggio al child dopo il segnale di avvenuto caricamento.
   * La ricezione di tali informazioni vanno gestite lato child
   * attraverso l'intercettazione del messaggio **PluginMessageTypes.EXTRA_PARAMS**  
   * perchè funzioni correttamente l'hook per intercettarlo deve essere posizionato in un componente che viene renderizzato subito
   * consigliato posizionarlo dove viene usato *PluginApp* */
  extraParams: PropTypes.shape({}),
  /** stile che sovrascrive i valori di default */
  style: PropTypes.shape({
    width: PropTypes.string,
    height: PropTypes.string,
  }),
  /**
   * Callback invocata dopo il caricamento del contenuto:
   * ```
   *  onLoaded(result)
   * ```
   * dove:
   * * *result*: **true**=caricato con successo; **false**=errore durante il caricamento
   */
  onLoaded: PropTypes.func,
  /**
   * Callback invocata ogni volta che viene inviato un messaggio al parent:
   * ```
   *  onReceiveMessage(message)
   * ```
   * dove:
   * * *message*: messaggio ricevuto
   */
  onReceiveMessage: PropTypes.func,
  /** Parametro che determina se deve essere gestito il cambio di hash dal parent e viceversa */
  updateHash: PropTypes.bool,
  /** @ignore utilizzato dal componente ModalIframe */
  onDialogResize: PropTypes.func,
  /** @ignore utilizzato dal componente ModalIframe */
  onDialogOk: PropTypes.func,
  /** @ignore utilizzato dal componente ModalIframe */
  onDialogCancel: PropTypes.func,
  /** Parametro utilizzato per disabilitare il controllo se
   * l'applicativo descritto in *src* è stato caricato */
  checkLoaded: PropTypes.bool,
  /** Parametro utilizzato per disabilitare la visualizzazione di uno
   * spinner durante il caricamento del contenuto */
  showSpinner: PropTypes.bool,
  /** Parametro utilizzato per permettere al child di poter bloccare
   * gli eventi click sul parent */
  checkClickableOutside: PropTypes.bool,
  /**
   * Callback invocata ogni volta che il child richiede una redirect ad un url esterno:
   * ```
   *  onRedirectUrl(url)
   * ```
   * dove:
   * * *url*: indirizzo richiesto
   *
   * Se non definita la funzione, per default viene fatto un redirect
   */
  onRedirectUrl: PropTypes.func,
  /**
   * Da settare se il routing è gestito in memoria (es. MemoryRouter).
   * In presenza di memory routing non aggiorna l'hash della window
   */
  inMemoryRouting: PropTypes.bool,
};

export default IFrame;
